/**
 * Admin Beneficiaries API Client
 *
 * Provides methods for managing insurance beneficiaries in admin/backoffice context.
 * All fetch methods unwrap the standard API envelope
 * { success, message, timestamp, data: T } and return T directly.
 */

import type {
  AdminBeneficiary,
  AdminBeneficiaryActionFormData,
} from "../types/admin-beneficiaries.types";

// ─── Response types (unwrapped — match the `data` field of the API envelope) ─

export interface AdminBeneficiariesResponse {
  beneficiaries: AdminBeneficiary[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

// ─── Fetch infrastructure ─────────────────────────────────────────────────────

export type AdminBeneficiariesFetchFunction = (
  url: string,
  options?: RequestInit,
) => Promise<Response>;

function defaultFetch(url: string, options?: RequestInit): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
}

let _fetch: AdminBeneficiariesFetchFunction = defaultFetch;

export function configureAdminBeneficiariesApi(
  fetchFn: AdminBeneficiariesFetchFunction,
): void {
  _fetch = fetchFn;
}

async function unwrap<T>(response: Response): Promise<T> {
  const body = await response.json();
  if (!response.ok) {
    throw new Error(
      body?.error?.message || `HTTP ${response.status}: ${response.statusText}`,
    );
  }
  return body.data as T;
}

// ─── API client ───────────────────────────────────────────────────────────────

export const adminBeneficiariesApi = {
  async fetchAll(
    params?: {
      limit?: number;
      offset?: number;
      status?: string;
      relationship?: string;
      designation?: string;
      search?: string;
    },
    baseUrl = "/api/v1",
  ): Promise<AdminBeneficiariesResponse> {
    const query = new URLSearchParams();
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.offset) query.set("offset", String(params.offset));
    if (params?.status) query.set("status", params.status);
    if (params?.relationship) query.set("relationship", params.relationship);
    if (params?.designation) query.set("designation", params.designation);
    if (params?.search) query.set("search", params.search);
    const qs = query.toString();
    const res = await _fetch(
      `${baseUrl}/admin/beneficiaries${qs ? `?${qs}` : ""}`,
    );
    return unwrap<AdminBeneficiariesResponse>(res);
  },

  async fetchById(id: string, baseUrl = "/api/v1"): Promise<AdminBeneficiary> {
    const res = await _fetch(`${baseUrl}/admin/beneficiaries/${id}`);
    return unwrap<AdminBeneficiary>(res);
  },

  async updateVerificationStatus(
    id: string,
    data: AdminBeneficiaryActionFormData,
    baseUrl = "/api/v1",
  ): Promise<AdminBeneficiary> {
    if (data.action === "verify") {
      const res = await _fetch(`${baseUrl}/admin/beneficiaries/${id}/verify`, {
        method: "PATCH",
        body: JSON.stringify({ notes: data.notes }),
      });
      return unwrap<AdminBeneficiary>(res);
    }

    const res = await _fetch(`${baseUrl}/admin/beneficiaries/${id}/reject`, {
      method: "PATCH",
      body: JSON.stringify({
        reason: data.rejectionReason,
        notes: data.notes,
      }),
    });
    return unwrap<AdminBeneficiary>(res);
  },

  async requestDocuments(
    id: string,
    data: { notes?: string },
    baseUrl = "/api/v1",
  ): Promise<AdminBeneficiary> {
    const res = await _fetch(`${baseUrl}/admin/beneficiaries/${id}/verify`, {
      method: "PATCH",
      body: JSON.stringify({
        status: "pending_documents",
        notes: data.notes,
      }),
    });
    return unwrap<AdminBeneficiary>(res);
  },

  async updateBeneficiary(
    id: string,
    data: Partial<AdminBeneficiary>,
    baseUrl = "/api/v1",
  ): Promise<AdminBeneficiary> {
    const res = await _fetch(`${baseUrl}/admin/beneficiaries/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return unwrap<AdminBeneficiary>(res);
  },
};
